import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useSchool } from "@/context/SchoolContext";
import { BACKEND_URL } from "@/constants/api";
import { BookOpen, ChevronLeft, AlertCircle, User, UserCog } from "lucide-react";
import { PageContainer } from "@/layouts/PageContainer";
import ClassList from "@/components/entities/ClassList";
import { Button } from "@/components/ui/button";
import { Modal, ModalBody, ModalFooter } from "@/components/modals/Modal";

function truncateOen(raw) {
  const trimmed = raw?.trim() ?? "";
  if (!trimmed) return null;
  return trimmed.length > 9 ? trimmed.slice(0, 9) + "…" : trimmed;
}

function ReadOnlyReportRow({ report }) {
  const oenDisplay = truncateOen(report.data?.oen);
  return (
    <div className="rounded-xl overflow-hidden bg-white border dark:bg-primary dark:border-transparent flex items-center">
      <div className="flex-1 flex items-center px-4 py-3 gap-4 min-w-0">
        <div className="w-[100px] shrink-0">
          {oenDisplay ? (
            <span className="block text-center text-sm bg-brand-50 border border-brand-200 text-brand-700 dark:bg-white/10 dark:border-white/20 dark:text-white/100 rounded-md px-2 py-0.5 font-mono">
              {oenDisplay}
            </span>
          ) : (
            <span className="block text-center text-sm bg-brand-50/50 border border-brand-100 text-neutral-400 italic dark:bg-white/10 dark:border-white/20 dark:text-white/30 rounded-md px-2 py-0.5">
              No OEN
            </span>
          )}
        </div>
        <p className="flex-1 min-w-0 text-sm font-medium text-neutral-900 dark:text-white truncate">
          {report.studentName?.trim() || "Unnamed student"}
        </p>
        <span className="text-xs text-muted-foreground shrink-0">{report.reportType}</span>
      </div>
    </div>
  );
}

function AssignOwnerModal({ open, onClose, schoolId, classId, currentOwnerId, onSuccess }) {
  const { getToken } = useAuth();
  const [members, setMembers] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedId("");
    (async () => {
      setLoading(true);
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/members`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to load members");
        const data = await res.json();
        setMembers(data.filter((m) => m._id !== currentOwnerId));
      } catch {
        toast.error("Could not load school members");
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [open, schoolId, currentOwnerId, getToken, onClose]);

  async function handleSubmit() {
    if (!selectedId) return;
    setSubmitting(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/classes/${classId}/owner`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ newOwnerId: selectedId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Transfer failed");
      }
      toast.success("Class ownership transferred");
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Assign New Owner"
      subtitle="Transfer this class to another teacher in your school."
      icon={<UserCog size={16} className="text-primary" />}
      maxWidth="max-w-sm"
    >
      <ModalBody>
        {loading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => <div key={i} className="h-10 bg-muted animate-pulse rounded-lg" />)}
          </div>
        ) : members.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No other teachers in this school.
          </p>
        ) : (
          <div className="space-y-1.5">
            {members.map((m) => (
              <button
                key={m._id}
                onClick={() => setSelectedId(m._id)}
                className={`w-full text-left rounded-lg border px-4 py-2.5 transition-colors ${
                  selectedId === m._id
                    ? "border-primary bg-primary/5 text-foreground"
                    : "border-border bg-card text-foreground hover:bg-muted/50"
                }`}
              >
                <p className="text-sm font-medium leading-tight">{m.name || "Unnamed"}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{m.email}</p>
              </button>
            ))}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button onClick={handleSubmit} disabled={!selectedId || submitting}>
          {submitting ? "Transferring…" : "Transfer Ownership"}
        </Button>
        <Button variant="outline" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function SchoolClassList({ schoolId }) {
  const { getToken } = useAuth();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/classes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to load classes");
        setClasses(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [schoolId, getToken]);

  if (loading) return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => <div key={i} className="h-14 bg-muted animate-pulse rounded-xl" />)}
    </div>
  );

  if (error) return (
    <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
      <AlertCircle size={15} />{error}
    </div>
  );

  if (classes.length === 0) return (
    <div className="flex flex-col items-center justify-center gap-2 py-14 text-center rounded-2xl border border-dashed border-border bg-muted/20">
      <BookOpen size={28} className="text-muted-foreground/40" />
      <p className="text-sm text-muted-foreground">No classes in this school yet</p>
    </div>
  );

  return (
    <ClassList
      items={classes}
      getLinkPath={(cls) => `/schools/classes/${cls._id}`}
      getOwnerName={(cls) => cls.ownerId?.name}
    />
  );
}

function ClassDetail({ schoolId, classId }) {
  const { getToken } = useAuth();
  const [school, setSchool] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [schoolRes, detailRes] = await Promise.all([
        fetch(`${BACKEND_URL}api/schools/${schoolId}`, { headers }),
        fetch(`${BACKEND_URL}api/schools/${schoolId}/classes/${classId}`, { headers }),
      ]);
      if (!detailRes.ok) {
        const body = await detailRes.json().catch(() => ({}));
        throw new Error(body.message || "Failed to load class");
      }
      if (schoolRes.ok) setSchool(await schoolRes.json());
      setDetail(await detailRes.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId, classId, getToken]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <PageContainer>
      <div className="h-8 w-40 bg-muted animate-pulse rounded mb-6" />
      <div className="h-48 bg-muted animate-pulse rounded-2xl" />
    </PageContainer>
  );

  if (error) return (
    <PageContainer>
      <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
        <AlertCircle size={15} />{error}
      </div>
    </PageContainer>
  );

  const label =
    detail?.name ||
    [detail?.grade && `Grade ${detail.grade}`, detail?.schoolYear].filter(Boolean).join(" · ");

  return (
    <PageContainer>
      <AssignOwnerModal
        open={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        schoolId={schoolId}
        classId={classId}
        currentOwnerId={detail?.ownerId?._id}
        onSuccess={load}
      />

      <Link
        to="/schools/classes"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft size={14} />
        {school?.name} - Classes
      </Link>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <BookOpen size={18} className="text-primary" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-normal text-foreground">{label}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {[detail?.grade && `Grade ${detail.grade}`, detail?.schoolYear, detail?.ownerId?.name]
                .filter(Boolean).join(" · ")}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0 gap-1.5"
          onClick={() => setShowAssignModal(true)}
        >
          <UserCog size={14} />
          Assign New Owner
        </Button>
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-border">
          <p className="text-sm font-medium text-foreground">
            Students ({detail?.reports?.length ?? 0})
          </p>
        </div>
        {detail?.reports?.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
            <User size={28} className="text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No student reports yet</p>
          </div>
        ) : (
          <div className="p-4 space-y-2">
            {detail?.reports?.map((report) => (
              <ReadOnlyReportRow key={report._id} report={report} />
            ))}
          </div>
        )}
      </div>
    </PageContainer>
  );
}

export default function ManageClassOwnership() {
  const { memberships } = useSchool();
  const schoolId = memberships[0]?.school?._id;
  const { classId } = useParams();

  if (classId) {
    return <ClassDetail schoolId={schoolId} classId={classId} />;
  }

  return (
    <PageContainer>
      <Link
        to="/schools"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft size={14} />
        My School
      </Link>
      <h1 className="font-display text-2xl font-normal text-foreground mb-6">All Classes</h1>
      <SchoolClassList schoolId={schoolId} />
    </PageContainer>
  );
}
